import { z } from 'zod';
import { useState, type ChangeEvent } from 'react';

import { api } from '../../services/api/client';
import {
  installationStore,
  type LocalInstallation,
} from '../../services/persistence/identity-store';
import {
  canUseDebugFixtureImport,
  resolveDebugApiOrigin,
} from './debug-fixture-import-availability';

const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/+$/, '');

const uuidSchema = z.uuid();
const tokenSchema = z.string().regex(/^[a-f0-9]{64}$/);
const fixtureGroupSchema = z
  .object({
    groupId: uuidSchema,
    participantId: uuidSchema,
    membershipId: uuidSchema,
    legacySessionToken: tokenSchema,
  })
  .strict();
const fixturePersonSchema = z
  .object({
    personKey: z.string().regex(/^[a-z0-9][a-z0-9-]{0,17}$/),
    installationId: uuidSchema,
    installationToken: tokenSchema,
    legacySessionTokens: z.array(tokenSchema).length(2),
    profileId: uuidSchema,
    groups: z.array(fixtureGroupSchema).length(2),
  })
  .strict();
const fixtureManifestSchema = z
  .object({
    version: z.literal(1),
    fixtureId: uuidSchema,
    installationId: uuidSchema,
    installationToken: tokenSchema,
    legacySessionTokens: z.array(tokenSchema).length(2),
    profileId: uuidSchema,
    groups: z.array(fixtureGroupSchema).length(2),
    persons: z.array(fixturePersonSchema),
  })
  .strict();

type FixtureManifest = z.infer<typeof fixtureManifestSchema>;
type SelectedIdentity = {
  installationId: string;
  installationToken: string;
  profileId: string;
  groups: FixtureManifest['groups'];
  personKey: string;
};

function validateManifest(value: unknown): FixtureManifest {
  const parsed = fixtureManifestSchema.safeParse(value);
  if (!parsed.success) throw new Error('Das lokale Debug-Manifest ist ungültig.');
  const manifest = parsed.data;
  const groupIds = new Set(manifest.groups.map((group) => group.groupId));
  if (groupIds.size !== 2) throw new Error('Das lokale Debug-Manifest ist ungültig.');
  const personKeys = new Set<string>();
  const installationIds = new Set([manifest.installationId]);
  const profileIds = new Set([manifest.profileId]);
  for (const person of manifest.persons) {
    if (
      personKeys.has(person.personKey) ||
      installationIds.has(person.installationId) ||
      profileIds.has(person.profileId) ||
      new Set(person.groups.map((group) => group.groupId)).size !== 2 ||
      !person.groups.every((group) => groupIds.has(group.groupId))
    ) {
      throw new Error('Das lokale Debug-Manifest ist ungültig.');
    }
    personKeys.add(person.personKey);
    installationIds.add(person.installationId);
    profileIds.add(person.profileId);
  }
  return manifest;
}

function selectedIdentity(manifest: FixtureManifest, personKey: string): SelectedIdentity {
  if (personKey === 'base') {
    return {
      personKey,
      installationId: manifest.installationId,
      installationToken: manifest.installationToken,
      profileId: manifest.profileId,
      groups: manifest.groups,
    };
  }
  const person = manifest.persons.find((item) => item.personKey === personKey);
  if (person === undefined) throw new Error('Die synthetische Person wurde nicht gefunden.');
  return person;
}

function toLocalInstallation(
  selected: SelectedIdentity,
  bootstrap: Awaited<ReturnType<typeof api.installationBootstrap>>,
): LocalInstallation {
  if (bootstrap.profile.id !== selected.profileId) {
    throw new Error('Die lokale Identität konnte nicht bestätigt werden.');
  }
  const memberships = selected.groups.map((group) => {
    const membership = bootstrap.memberships.find(
      (item) =>
        item.id === group.membershipId &&
        item.groupId === group.groupId &&
        item.participantId === group.participantId &&
        item.leftAt === null,
    );
    if (membership === undefined) throw new Error('Die lokale Gruppenmitgliedschaft fehlt.');
    return {
      id: membership.id,
      groupId: membership.groupId,
      participantId: membership.participantId,
      groupName: membership.groupName,
      displayName: membership.displayName,
      joinedAt: membership.joinedAt,
      legacyIdentity: {
        groupId: membership.groupId,
        groupCode: '000000',
        participantId: membership.participantId,
        sessionToken: group.legacySessionToken,
        role: 'participant' as const,
      },
    };
  });
  return {
    installationId: selected.installationId,
    installationToken: selected.installationToken,
    profile: {
      weightKg: bootstrap.profile.weightKg,
      heightCm: bootstrap.profile.heightCm,
      notificationsEnabled: bootstrap.profile.notificationsEnabled,
    },
    memberships,
    lastSelectedGroupId: memberships[0]?.groupId ?? null,
  };
}

export function DebugFixtureImport({
  onComplete,
  apiOrigin = API_ORIGIN,
}: {
  onComplete: () => void;
  apiOrigin?: string;
}) {
  const available = canUseDebugFixtureImport(import.meta.env.DEV, window.location.hostname);
  const [manifest, setManifest] = useState<FixtureManifest | null>(null);
  const [personKey, setPersonKey] = useState('base');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!available) return null;

  async function readManifest(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file === undefined) return;
    setError(null);
    try {
      resolveDebugApiOrigin(apiOrigin);
      const parsed = validateManifest(JSON.parse(await file.text()));
      setManifest(parsed);
      setPersonKey('base');
    } catch {
      setManifest(null);
      setError('Die lokale Debug-Fixture-Datei konnte nicht validiert werden.');
    }
  }

  async function importSelected() {
    if (manifest === null) return;
    setBusy(true);
    setError(null);
    try {
      resolveDebugApiOrigin(apiOrigin);
      const selected = selectedIdentity(manifest, personKey);
      const bootstrap = await api.installationBootstrap(selected.installationToken);
      const installation = toLocalInstallation(selected, bootstrap);
      if (!installationStore.write(installation)) {
        throw new Error('Lokale Identität konnte nicht gespeichert werden.');
      }
      onComplete();
    } catch {
      setError('Die lokale Debug-Identität konnte nicht sicher importiert werden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="debug-fixture-import-title" className="debug-fixture-import">
      <p className="step-label">Lokale Entwicklung</p>
      <h2 id="debug-fixture-import-title">Debug-Fixture importieren</h2>
      <p>
        Nur für den lokalen Worker: Wähle deine ignorierte Debug-Fixture-Datei, um eine synthetische
        Person und ihre beiden Gruppen nach einem Neustart wiederherzustellen.
      </p>
      <label>
        Debug-Fixture-Datei
        <input
          type="file"
          accept="application/json,.json"
          onChange={(event) => void readManifest(event)}
        />
      </label>
      {manifest === null ? null : (
        <>
          <label>
            Identität auswählen
            <select value={personKey} onChange={(event) => setPersonKey(event.target.value)}>
              <option value="base">Basis-Person</option>
              {manifest.persons.map((person) => (
                <option key={person.personKey} value={person.personKey}>
                  Synthetische Person: {person.personKey}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button button--primary"
            disabled={busy}
            onClick={() => void importSelected()}
          >
            {busy ? 'Wird importiert …' : 'Debug-Fixture importieren'}
          </button>
        </>
      )}
      {error === null ? null : (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
    </section>
  );
}
